import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { 
  FileText,
  CheckCircle2,
  Clock,
  Download,
  Eye,
  Building2,
  Search,
  Mail,
  Send,
  Loader2
} from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ClientContracts() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  
  // Email modal state
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);
  const [emailForm, setEmailForm] = useState({
    recipient_email: "",
    recipient_name: "",
    custom_message: ""
  });
  const [sendingEmail, setSendingEmail] = useState(false);

  useEffect(() => {
    fetchContracts();
  }, [token]);

  // Filter contracts based on search query
  const filteredContracts = contracts.filter((contract) => {
    const query = searchQuery.toLowerCase();
    return (
      contract.project_title?.toLowerCase().includes(query) ||
      contract.contract_id?.toLowerCase().includes(query) ||
      contract.client_name?.toLowerCase().includes(query) ||
      contract.project_id?.toLowerCase().includes(query)
    );
  });

  const fetchContracts = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/contracts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!res.ok) throw new Error("Could not fetch contracts");
      
      const data = await res.json();
      setContracts(data);
    } catch (error) {
      console.error("Error fetching contracts:", error);
      toast.error("Kunne ikke laste kontrakter");
    } finally {
      setLoading(false);
    }
  };

  const handlePreviewContract = async (projectId) => {
    try {
      const res = await fetch(`${API_URL}/api/contracts-pdf/preview/client-holding/${projectId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!res.ok) throw new Error("Could not load contract preview");
      
      const htmlContent = await res.text();
      const newWindow = window.open('', '_blank');
      newWindow.document.write(htmlContent);
      newWindow.document.close();
    } catch (error) {
      console.error("Error previewing contract:", error);
      toast.error("Kunne ikke vise kontrakt");
    }
  };

  const handleDownloadContract = async (projectId, projectTitle) => {
    try {
      toast.info("Laster ned kontrakt...");
      
      const res = await fetch(`${API_URL}/api/contracts-pdf/client-holding/${projectId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!res.ok) throw new Error("Could not download contract");
      
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `kontrakt_${projectTitle || projectId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success("Kontrakt lastet ned!");
    } catch (error) {
      console.error("Error downloading contract:", error);
      toast.error("Kunne ikke laste ned kontrakt");
    }
  };

  const openEmailModal = (contract) => {
    setSelectedContract(contract);
    setEmailForm({
      recipient_email: "",
      recipient_name: "",
      custom_message: ""
    });
    setEmailModalOpen(true);
  };

  const handleSendEmail = async () => {
    if (!emailForm.recipient_email) {
      toast.error("Vennligst skriv inn e-postadresse");
      return;
    }
    
    try {
      setSendingEmail(true);
      
      const res = await fetch(
        `${API_URL}/api/contracts-pdf/email/client-holding/${selectedContract.project_id}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          },
          body: JSON.stringify(emailForm)
        }
      );
      
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || "Kunne ikke sende e-post");
      }
      
      toast.success(`Kontrakt sendt til ${emailForm.recipient_email}!`);
      setEmailModalOpen(false);
    } catch (error) {
      console.error("Error sending email:", error);
      toast.error(error.message || "Kunne ikke sende e-post");
    } finally {
      setSendingEmail(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("no-NO", {
      day: "2-digit",
      month: "long",
      year: "numeric"
    });
  };

  const getStatusBadge = (status) => {
    const configs = {
      pending: { label: "Venter", color: "bg-yellow-500/20 text-yellow-400", icon: Clock },
      signed: { label: "Signert", color: "bg-green-500/20 text-green-400", icon: CheckCircle2 },
    };
    
    const config = configs[status] || configs.pending;
    const Icon = config.icon;
    
    return (
      <Badge className={`${config.color} border-0`}>
        <Icon className="w-3 h-3 mr-1" />
        {config.label}
      </Badge>
    );
  };

  const stats = {
    total: contracts.length,
    signed: contracts.filter(c => c.status === "signed").length,
    pending: contracts.filter(c => c.status === "pending").length,
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 p-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-white">Mine Kontrakter</h1>
            <p className="text-gray-400 mt-1">Oversikt over alle dine prosjektkontrakter</p>
          </div>
          
          {/* Search Input */}
          <div className="relative w-full md:w-80">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
            <Input
              type="text"
              placeholder="Søk etter prosjekt, kontrakt-ID..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-[#1a1a2e] border-[#2a2a4a] text-white placeholder:text-gray-500 focus:border-[#23F7DD]"
            />
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="bg-[#1a1a2e] border-[#2a2a4a]">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="p-3 bg-blue-500/20 rounded-lg">
                <FileText className="w-6 h-6 text-blue-400" />
              </div>
              <div>
                <p className="text-3xl font-bold text-blue-400">{stats.total}</p>
                <p className="text-sm text-gray-400">Totalt Kontrakter</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#1a1a2e] border-[#2a2a4a]">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="p-3 bg-green-500/20 rounded-lg">
                <CheckCircle2 className="w-6 h-6 text-green-400" />
              </div>
              <div>
                <p className="text-3xl font-bold text-green-400">{stats.signed}</p>
                <p className="text-sm text-gray-400">Signert</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#1a1a2e] border-[#2a2a4a]">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="p-3 bg-yellow-500/20 rounded-lg">
                <Clock className="w-6 h-6 text-yellow-400" />
              </div>
              <div>
                <p className="text-3xl font-bold text-yellow-400">{stats.pending}</p>
                <p className="text-sm text-gray-400">Venter</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Contracts Table */}
        <Card className="bg-[#1a1a2e] border-[#2a2a4a]">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="w-10 h-10 border-2 border-[#23F7DD] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : contracts.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="w-12 h-12 mx-auto text-gray-600 mb-4" />
                <p className="text-gray-400">Ingen kontrakter ennå</p>
                <p className="text-gray-500 text-sm mt-2">
                  Kontrakter vil vises her når du signerer prosjekter
                </p>
              </div>
            ) : filteredContracts.length === 0 ? (
              <div className="text-center py-12">
                <Search className="w-12 h-12 mx-auto text-gray-600 mb-4" />
                <p className="text-gray-400">Ingen resultater for &ldquo;{searchQuery}&rdquo;</p>
                <p className="text-gray-500 text-sm mt-2">
                  Prøv et annet søkeord
                </p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-[#2a2a4a] hover:bg-transparent">
                    <TableHead className="text-gray-400">Prosjekt</TableHead>
                    <TableHead className="text-gray-400">Kontrakt ID</TableHead>
                    <TableHead className="text-gray-400">Status</TableHead>
                    <TableHead className="text-gray-400">Opprettet</TableHead>
                    <TableHead className="text-gray-400">Signert</TableHead>
                    <TableHead className="text-gray-400 text-right">Handlinger</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredContracts.map((contract) => (
                    <TableRow key={contract.contract_id} className="border-[#2a2a4a]">
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-gray-400" />
                          <span className="text-white font-medium">
                            {contract.project_title || `Prosjekt ${contract.project_id?.slice(0, 8)}`}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="font-mono text-gray-400 text-sm">
                        {contract.contract_id?.slice(0, 16)}...
                      </TableCell>
                      <TableCell>
                        {getStatusBadge(contract.status)}
                      </TableCell>
                      <TableCell className="text-gray-400">
                        {formatDate(contract.created_at)}
                      </TableCell>
                      <TableCell className="text-gray-400">
                        {contract.signed_at ? formatDate(contract.signed_at) : "-"}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => navigate(`/client/projects-v3/${contract.project_id}`)}
                            className="text-blue-400 hover:text-blue-300"
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            Se Prosjekt
                          </Button>
                          {contract.status === "signed" && (
                            <>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handlePreviewContract(contract.project_id)}
                                className="text-purple-400 hover:text-purple-300"
                              >
                                <FileText className="w-4 h-4 mr-1" />
                                Se Kontrakt
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleDownloadContract(contract.project_id, contract.project_title)}
                                className="text-[#23F7DD] hover:text-[#70FFEF]"
                              >
                                <Download className="w-4 h-4 mr-1" />
                                Last ned
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => openEmailModal(contract)}
                                className="text-orange-400 hover:text-orange-300"
                              >
                                <Mail className="w-4 h-4 mr-1" />
                                Send
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Email Modal */}
      <Dialog open={emailModalOpen} onOpenChange={setEmailModalOpen}>
        <DialogContent className="bg-[#1a1a2e] border-[#2a2a4a] text-white sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Mail className="w-5 h-5 text-orange-400" />
              Send Kontrakt på E-post
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Send kontrakten for <span className="text-white font-medium">{selectedContract?.project_title}</span> til en mottaker.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-gray-300">E-postadresse *</Label>
              <Input
                id="email"
                type="email"
                placeholder="mottaker@firma.no"
                value={emailForm.recipient_email}
                onChange={(e) => setEmailForm({...emailForm, recipient_email: e.target.value})}
                className="bg-[#0d0d1a] border-[#2a2a4a] text-white placeholder:text-gray-500 focus:border-[#23F7DD]"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="name" className="text-gray-300">Mottakers navn (valgfritt)</Label>
              <Input
                id="name"
                type="text"
                placeholder="Ola Nordmann"
                value={emailForm.recipient_name}
                onChange={(e) => setEmailForm({...emailForm, recipient_name: e.target.value})}
                className="bg-[#0d0d1a] border-[#2a2a4a] text-white placeholder:text-gray-500 focus:border-[#23F7DD]"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="message" className="text-gray-300">Melding (valgfritt)</Label>
              <Textarea
                id="message"
                placeholder="Vennligst se vedlagt kontrakt for prosjektet..."
                value={emailForm.custom_message}
                onChange={(e) => setEmailForm({...emailForm, custom_message: e.target.value})}
                className="bg-[#0d0d1a] border-[#2a2a4a] text-white placeholder:text-gray-500 focus:border-[#23F7DD] min-h-[80px]"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEmailModalOpen(false)}
              className="border-[#2a2a4a] text-gray-400 hover:text-white"
            >
              Avbryt
            </Button>
            <Button
              onClick={handleSendEmail}
              disabled={sendingEmail || !emailForm.recipient_email}
              className="bg-orange-500 hover:bg-orange-600 text-white"
            >
              {sendingEmail ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sender...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send Kontrakt
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
